import { heroDelivery, heroPreloadLinks } from "@/lib/hero-delivery";
import { RichText } from "@/components/site/RichText";
import { LocaleLink, type PublicPath } from "@/components/site/LocaleLink";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { Overline } from "@/components/site/Overline";
import { ServiceBand } from "@/components/site/ServiceBand";
import { Droplets, Search, Wrench, ShieldCheck, ArrowRight, Snowflake, Sun } from "lucide-react";
import { ProjectGrid } from "@/components/site/ProjectGrid";
import techniek1 from "../../assets/techniek/techniek-1.avif.asset.json";
import techniek2 from "../../assets/techniek/techniek-2.avif.asset.json";
import techniek3 from "../../assets/techniek/techniek-3.avif.asset.json";
import techniek4 from "../../assets/techniek/techniek-4.avif.asset.json";
import techniek5 from "../../assets/techniek/techniek-5.avif.asset.json";
import techniek6 from "../../assets/techniek/techniek-6.avif.asset.json";
import { FaqSection } from "@/components/site/FaqSection";
import { faqs, faqJsonLd } from "@/content/faqs";
import { canonical, breadcrumbJsonLd, serviceJsonLd, shareImageMeta, langPageMeta } from "@/content/site";
import { business, responseTime } from "@/content/site";
import { publishedPageQuery } from "@/lib/content-queries";
import { pageContent } from "@/lib/page-content";
import { usePageContent, validateVoorvertoning } from "@/lib/use-page-content";
import { PhotoBlocks } from "@/components/site/PhotoBlocks";

const PATH = "/technieken";
const SLEUTEL = "technieken";

const HERO_FALLBACK = { src: techniek4.url };

const CODE_META_TITLE = "Zwembad lekdetectie & filtratie | Limburg";
const CODE_META_DESCRIPTION =
  "Lekdetectie zonder onnodig graafwerk, herstel van leidingwerk, filtratie en winter- en zomeronderhoud voor alle zwembaden in Limburg en Zuid-Nederland.";

export const Route = createFileRoute("/$lang/technieken")({
  staticData: { sitemap: true },
  validateSearch: validateVoorvertoning,
  loader: async ({ context, params }) => ({
    page: await context.queryClient.ensureQueryData(publishedPageQuery(SLEUTEL, params.lang)),
  }),
  head: ({ params, loaderData }) => {
    const c = pageContent(loaderData?.page);
    const title = c.metaTitle(CODE_META_TITLE);
    const description = c.metaDescription(CODE_META_DESCRIPTION);
    const heroPhoto = heroDelivery(c.section("hero"), HERO_FALLBACK);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: "Lekdetectie, herstel van zwembadleidingen, filtratie en seizoensonderhoud." },
        { property: "og:type", content: "website" },
        ...langPageMeta(params.lang, PATH).meta,
        { name: "twitter:card", content: "summary_large_image" },
        ...shareImageMeta("technieken", "Zwembadtechniek: filtratie, leidingwerk en lekdetectie"),
      ],
      links: [...langPageMeta(params.lang, PATH).links, ...heroPreloadLinks(heroPhoto)],
      scripts: [
        faqJsonLd(faqs.technieken),
        breadcrumbJsonLd("Technieken & lekdetectie", PATH),
        serviceJsonLd({
          name: "Zwembadtechniek, lekdetectie en onderhoud",
          serviceType: "Zwembadonderhoud en lekdetectie",
          description:
            "Lekdetectie met druktesten, akoestische detectie en thermografie, herstel van leidingwerk, filtratie-installaties en winter- en zomeronderhoud.",
          path: PATH,
        }),
      ],
    };
  },
  component: TechniekenPage,
});

const serviceIcons = [Droplets, Search, Wrench, ShieldCheck];

const services = [
  {
    titel: "Filtratie & waterbehandeling",
    tekst:
      "Zandfilters, glasmediafilters, zoutelektrolyse en doseringssystemen: wij installeren en onderhouden de installatie die bij uw bad past.",
  },
  {
    titel: "Lekdetectie",
    tekst:
      "Met druktesten, akoestische detectie en thermografie lokaliseren wij lekkages in leidingwerk, skimmers, spotjes en de kuip zelf.",
  },
  {
    titel: "Herstel van leidingwerk",
    tekst:
      "Van vervangen van defecte fittingen tot het vernieuwen van ondergrondse leidingen bij renovaties. Wij werken snel en netjes, met minimale verstoring van uw tuin.",
  },
  {
    titel: "Onderhoud & nazorg",
    tekst:
      "Jaarlijks onderhoud, winterklaar maken en opstarten in het voorjaar. Zo blijft uw zwembad jarenlang in topconditie.",
  },
];

const lekdetectiePunten = [
  "Nauwkeurige detectie zonder onnodig graafwerk",
  "Duidelijke rapportage en prijsopgave",
  "Herstel in eigen beheer",
];

function TechniekenPage() {
  const { lang } = Route.useParams();
  const { voorvertoning } = Route.useSearch();
  const c = usePageContent(SLEUTEL, lang, voorvertoning);
  const hero = c.section("hero");
  const heroPhoto = heroDelivery(hero, HERO_FALLBACK);

  return (
    <div className="flex flex-col">
      <PageHero
        title={c.veld("hero", "titel", "Techniek & lekdetectie")}
        subtitle={c.rijkInline("hero",
          "Een zwembad is maar zo goed als de techniek erachter. Wij installeren, onderhouden en herstellen filtratie, leidingwerk en lekkages in heel Limburg en Zuid-Nederland — ook bij zwembaden die wij niet gebouwd hebben.",
        )}
        imageSrc={heroPhoto.src}
        imageFocal={heroPhoto.focal}
        imageSrcSet={heroPhoto.srcSet}
        imageAlt={c.veld(
          "hero",
          "alt",
          "Afgewerkte technische ruimte met OCEA-besturing, doseersysteem, zwembadpomp en zandfilter tegen een donkere wand",
        )}
        cta={{
          label: c.veld("hero", "knopTekst", "Meld een lekkage"),
          to: c.veld("hero", "knopLink", "/offerte") as PublicPath,
        }}
        secondaryCta={{
          label: c.veld("hero", "knop2Tekst", "Bel direct"),
          to: c.veld("hero", "knop2Link", `tel:${business.phoneE164}`) as PublicPath,
        }}
      />

      <Section tone="light">
        <div className="max-w-[70ch]">
          <Overline>{c.veld("lek", "overline", "Lek opsporen")}</Overline>
          <h2 className="mt-4 display-section">{c.veld("lek", "titel", "Hoe weet ik of mijn zwembad lekt?")}</h2>
          {c
            .alineas("lek", [
              "Verliest uw zwembad meer dan ongeveer 3 mm water per dag bovenop de normale verdamping, dan is er waarschijnlijk een lek. Een eenvoudige emmertest geeft uitsluitsel: zet een emmer water op de trap, markeer het peil binnen en buiten de emmer en vergelijk na 24 uur.",
              "Andere signalen zijn een steeds natte of verzakte zone in de tuin, vocht in de technische ruimte, een pomp die lucht aanzuigt of een filter die aan druk verliest. Zakt het water tot net onder de skimmer en stopt het daar, dan zit het lek vaak ter hoogte van de skimmer of de doorvoer.",
            ])
            .map((alinea, index) => (
              <p
                key={alinea.slice(0, 24)}
                className={`${index === 0 ? "mt-6" : "mt-4"} text-[17px] leading-7 text-on-surface-variant`}
              >
                <RichText waarde={alinea} inline />
              </p>
            ))}

          <h3 className="mt-10 font-display text-xl font-semibold">
            {c.veld("opsporen", "titel", "Zo sporen wij het lek op")}
          </h3>
          <p className="mt-4 text-[17px] leading-7 text-on-surface-variant">
            {c.rijkInline("opsporen",
              "Wij isoleren eerst de kringen en zetten het leidingwerk onder druk om uit te sluiten of het probleem in de kuip of in de leidingen zit. Daarna lokaliseren wij het punt met akoestische detectie en, waar nuttig, thermografie. Zo graven wij gericht op één plek in plaats van de hele tuin open te leggen. U krijgt een duidelijk verslag en een prijsopgave vóór wij herstellen.",
            )}
          </p>

          <h3 className="mt-10 font-display text-xl font-semibold">
            {c.veld("filtratie", "titel", "Welke filtratie past bij uw bad?")}
          </h3>
          <p className="mt-4 text-[17px] leading-7 text-on-surface-variant">
            {c.rijkInline("filtratie",
              "Voor de meeste privézwembaden is een zandfilter de basis. Vervangt u het zand door glasmedia, dan filtert de installatie fijner, spoelt u minder water weg en gaat het medium langer mee. Zoutelektrolyse maakt het chloor zelf aan uit een lage zoutconcentratie: zachter water, minder chemie kopen en sjouwen. Wij dimensioneren pomp, filter en leidingdiameters altijd op de inhoud van uw bad, zodat het water minstens twee keer per dag volledig rondgaat.",
            )}
          </p>

          <h3 className="mt-10 font-display text-xl font-semibold">
            {c.veld("seizoen", "titel", "Winter- en zomeronderhoud")}
          </h3>
          <p className="mt-4 text-[17px] leading-7 text-on-surface-variant">
            {c.rijkInline("seizoen",
              "Bij het winterklaar maken verlagen wij het waterpeil, blazen wij de leidingen leeg of beveiligen ze tegen vorst, reinigen wij de filter, stellen wij het water chemisch in en plaatsen wij de winterafdekking. In het voorjaar starten wij de installatie op, reinigen het bad, controleren pomp, filter, verlichting en rolluik en brengen de waterbalans in orde zodat u meteen kunt zwemmen.",
            )}
          </p>
        </div>
      </Section>

      <ServiceBand
        items={c.kaarten("diensten", services).map((kaart, index) => ({
          icon: serviceIcons[index % serviceIcons.length]!,
          title: kaart.titel,
          text: kaart.tekst,
        }))}
      />

      <Section tone="light">
        <div className="max-w-[70ch]">
          <Overline>{c.veld("techniekruimte", "overline", "Technische ruimte")}</Overline>
          <h2 className="mt-4 display-section">
            {c.veld("techniekruimte", "titel", "Zo kan uw technische ruimte eruitzien")}
          </h2>
          <p className="mt-6 text-[17px] leading-7 text-on-surface-variant">
            {c.rijkInline("techniekruimte",
              "Filtratie, dosering en besturing hebben wij liever netjes en overzichtelijk. Zo ziet u op één blik hoe uw installatie werkt en blijft elk onderhoud eenvoudig.",
            )}
          </p>
        </div>
        <ProjectGrid
          className="mt-12"
          items={c.fotos("techniekruimte", [
            {
              src: techniek4.url,
              alt: "Technische ruimte met donker wandpaneel, zandfilter, pomp, OCEA-besturing en doseersysteem voor pH-regeling",
              caption: "OCEA-besturing met doseersysteem",
            },
            {
              src: techniek1.url,
              alt: "Technische ruimte met PVC-leidingwerk, zandfilter, zwembadpomp en WaterSens-doseersysteem tegen een lichte muur",
              caption: "Filtratie met automatische waterbehandeling",
            },
            {
              src: techniek2.url,
              alt: "Technische ruimte in cellenbeton met OCEA-besturing, doseersysteem, zandfilter en zekeringkast",
              caption: "Besturing, filtratie en dosering samen",
            },
            {
              src: techniek3.url,
              alt: "Vers geplaatste zwembadinstallatie met doseersysteem, PVC-verdeling en zandfilter tegen een houten wand",
              caption: "Vers geplaatste installatie",
            },
            {
              src: techniek5.url,
              alt: "Compacte technische ruimte in een houten berging met doseersysteem, PVC-leidingwerk en zandfilter",
              caption: "Compacte technische ruimte",
            },
            {
              src: techniek6.url,
              alt: "Zwembadtechniek in ruwbouw met leidingwerk, zandfilter en OCEA-besturing tegen oranje metselwerk",
              caption: "Leidingwerk en filter in ruwbouw",
            },
          ])}
        />
      </Section>

      <Section tone="mist">
        <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Overline>{c.veld("lekdetectie", "overline", "Lekdetectie")}</Overline>
            <h2 className="mt-4 display-section">{c.veld("lekdetectie", "titel", "Verdacht van een lekkage?")}</h2>
            <p className="mt-6 max-w-[70ch] text-[17px] leading-7 text-on-surface-variant">
              {c.rijkInline("lekdetectie",
                "Een dalend waterpeil, vochtplekken in de tuin of een continue bijvullend systeem zijn vaak signalen van een lek. Hoe sneller wij kunnen detecteren, hoe kleiner de schade.",
              )}
            </p>
            <ul className="mt-8 divide-y divide-border border-y border-border text-on-surface-variant">
              {c.lijst("lekdetectie", lekdetectiePunten).map((punt) => (
                <li key={punt} className="flex items-start gap-3 py-4">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-deep-water" strokeWidth={1.75} />
                  <span className="text-sm leading-6">{punt}</span>
                </li>
              ))}
            </ul>
            <Button asChild className="mt-8">
              <LocaleLink to={c.veld("lekdetectie", "knopLink", "/offerte") as PublicPath}>
                {c.veld("lekdetectie", "knopTekst", "Lekkage melden")} <ArrowRight className="ml-2 h-4 w-4" />
              </LocaleLink>
            </Button>
          </div>
          <div className="space-y-6">
            <div className="bg-primary p-8 text-primary-foreground lg:p-10">
              <Overline onDark>{c.veld("spoed", "overline", "Spoed")}</Overline>
              <h3 className="mt-4 font-display text-xl font-semibold">
                {c.veld("spoed", "titel", "Direct contact voor spoed")}
              </h3>
              <p className="mt-4 text-[17px] leading-7 text-primary-foreground/80">
                {c.rijkInline("spoed",
                  `Bel of WhatsApp ons voor een eerste inschatting. Wij reageren ${responseTime.within}.`,
                )}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
                  <a href={`tel:${business.phoneE164}`}>{business.phone}</a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-primary-foreground/60 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                >
                  <a href="https://wa.me/32484976477">WhatsApp</a>
                </Button>
              </div>
            </div>
            <div className="border border-border bg-background p-8 lg:p-10">
              <div className="mb-4 flex items-center gap-3 text-brand-deep-water">
                <Snowflake className="h-6 w-6" strokeWidth={1.75} />
                <Sun className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-xl font-semibold">
                {c.veld("onderhoud", "titel", "Winter- en zomeronderhoud")}
              </h3>
              <p className="mt-4 text-[17px] leading-7 text-on-surface-variant">
                {c.rijkInline("onderhoud",
                  "Wij bieden seizoensgebonden onderhoud aan: winterklaar maken, opstarten in het voorjaar, filterreiniging en wateranalyse. Zo blijft uw zwembad het hele jaar door in topconditie.",
                )}
              </p>
            </div>
          </div>
        </div>
      </Section>
      <PhotoBlocks content={c} />
      <FaqSection items={faqs.technieken} />
    </div>
  );
}
